import { debug, warn } from "rtUtils/logger";
import merge from "rtUtils/merge";
import { VIEWERS } from "../consts";
import buildUrl from "./shared/buildUrl";
import { appendMediaElement, updateMediaElement } from "./shared/dom";

const EVENTS = {
	VID_START: "tag-video-load-start",
	VID_SUCCESS: "tag-video-load-success",
	VID_ERROR: "tag-video-load-error",
};

const initVideoViewer = ({ getConfig }, { triggerWithAnalytics }) => {
	const store = new WeakMap();

	const render = (containers, existing = false) => {
		const config = getConfig();

		containers.forEach(async ({ container, containerProps }) => {
			const vidUrl = await buildUrl(containerProps, config, triggerWithAnalytics);
			debug(`Cloudinary 3D - VIDEO VIEWER - loading video from: ${vidUrl}`);

			const vidEventData = {
				url: vidUrl,
				containerProps,
				identifier: `#${container.id}.${container.className}`,
			};

			triggerWithAnalytics(EVENTS.VID_START, vidEventData);

			const params = [
				"video",
				vidUrl,
				merge({}, containerProps.tagParams, config.videoViewer.params),
				container,
			];

			const vidElm = existing || store.get(container) ?
				updateMediaElement(...params) :
				appendMediaElement(...params);

			const removeListeners = () => {
				vidElm.removeEventListener("loadeddata", onVidLoad);
				vidElm.removeEventListener("error", onVidErr);
			};

			const onVidLoad = () => {
				triggerWithAnalytics(EVENTS.VID_SUCCESS, vidEventData);
				removeListeners();
			};

			const onVidErr = () => {
				triggerWithAnalytics(EVENTS.VID_ERROR, { ...vidEventData, error: true });
				removeListeners();
			};

			vidElm.addEventListener("loadeddata", onVidLoad);
			vidElm.addEventListener("error", onVidErr);

			store.set(container, { containerProps });
		});
	};

	return {
		type: VIEWERS.VIDEO,

		render,

		update: (containers) => {
			render(containers.map(({ container, containerProps }) => {
					const instance = store.get(container);

					if (!instance) {
						warn(`Cloudinary 3D Video instance not found for container`, container);
					}

					return instance && {
						container,
						containerProps: merge({}, instance.containerProps, containerProps),
					};
				})
					.filter(Boolean),
				true);
		},

		remove: (containers) => {
			containers.forEach((container) => {
				const vid = container.querySelector("video");
				if (vid) {
					container.removeChild(vid);
				}
			});
		},

		getConfig: (container) => {
			const instance = store.get(container);

			if (!instance) {
				warn(`Cloudinary 3D Video instance not found for container`, container);
			}

			return instance &&
				merge({}, instance.containerProps, getConfig().videoViewer);
		},
	};
};

export default initVideoViewer;
